import React, { useState, useEffect, useRef } from 'react';
import './css/Navbar.css';
import logo_mat from './images/logo_nqtb_300x300.png';
import { IoLogOutOutline, IoPencilOutline, IoMailOutline, IoHelp, IoPersonOutline } from 'react-icons/io5';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import authService from '../auth/authService';
import labService from './labService';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const [user, setUser] = useState(() => {
    const savedData = localStorage.getItem('user');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        // Added is_staff here just in case it's stored locally to prevent UI jumping
        return { 
          email: parsed.email || "", 
          is_staff: parsed.is_staff || false 
        };
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  useEffect(() => {
    const fetchFullUserData = async () => {
      const userData = localStorage.getItem('user');
      if (!userData) {
        setLoading(false);
        return;
      }

      const parsedUserData = JSON.parse(userData);
      const token = parsedUserData.access;
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await labService.getUserInfo(token);
        // Assuming response.data contains { ..., is_staff: true/false }
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFullUserData();

    const handleClickOutside = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target) &&
        menuButtonRef.current && !menuButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);
  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isMenuOpen) setIsMenuOpen(false);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    authService.logout();
    window.location.reload();
  };

  return (
    <>
      <nav className="navbar">
        <div className="left">
          <Link to="/">
            <img className="logo-nav" src={logo_mat} alt="logo" />
          </Link>
        </div>

        <nav className="center-nav">
          {/* Conditionally spread the admin routes into the array if user.is_staff is true */}
          {[
            { name: 'Laboratory', path: '/laboratory' },
            { name: 'Licenses', path: '/wallet' },
            ...(user?.is_staff ? [
              { name: 'Manage licenses', path: '/manage-licences' },
              { name: 'Manage models', path: '/manage-models' }
            ] : [])
          ].map((route) => {
            const isActive = location.pathname.startsWith(route.path);
            return (
              <Link key={route.name} to={route.path} className="tab-link-wrapper">
                <span className={`tab-link-text ${isActive ? 'active-nav' : ''}`}>
                  {route.name}
                </span>
                <div className={`tab-underline ${isActive ? 'visible' : ''}`} />
              </Link>
            );
          })}
        </nav>

        <div className="right">
          <p className="user-email">
            {user?.email || ""}
          </p>

          <div className='user-menu'>
            <button
              aria-label="Options"
              className="custom-menu-button"
              onClick={toggleMenu}
              ref={menuButtonRef}
            >
              <IoPersonOutline size="1.5rem" color='white' />
            </button>

            <div className={`dropdown-menu ${isMenuOpen ? 'open' : ''}`} ref={menuRef}>
              <div className="menu-item" onClick={() => handleMenuItemClick('/user-form')}>
                <IoPencilOutline className="menu-icon blue-icon" />
                <span className="menu-text">Edit company data</span>
              </div>

              <div className="menu-item" onClick={() => handleMenuItemClick('/contact')}>
                <IoMailOutline className="menu-icon blue-icon" />
                <span className="menu-text">Contact us</span>
              </div>

              <div className="menu-item" onClick={() => window.open("https://sites.google.com/view/nqtb-faq", "_blank")}>
                <IoHelp className="menu-icon blue-icon" />
                <span className="menu-text">FAQ</span>
              </div>

              <div className="menu-item logout-item" onClick={handleLogout}>
                <IoLogOutOutline className="red-icon menu-icon" />
                <span className="menu-text red-text">Logout</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {isModalOpen && (
        <div className="modal-overlay" onClick={toggleModal}>
        </div>
      )}
    </>
  );
};

export default Navbar;